import {
  SupportPageContext,
  validatedSupportPageContext
} from './supportLaunchContext'

const PREFIX = 'support-ticket-draft:v1:'
const TTL = 30 * 60 * 1000
const OPTIONAL_STRING_FIELDS = [
  'entityType',
  'entityCode',
  'traceId',
  'traceFrom',
  'traceTo',
  'traceServices'
] as const
type OptionalDraftFields = Partial<
  Record<(typeof OPTIONAL_STRING_FIELDS)[number], string>
> & { attachTrace?: boolean }

function optionalDraftFields(values: OptionalDraftFields): OptionalDraftFields {
  const out: OptionalDraftFields = {}
  for (const field of OPTIONAL_STRING_FIELDS) {
    const value = values[field]
    if (typeof value === 'string' && value.length <= 1024) out[field] = value
  }
  if (typeof values.attachTrace === 'boolean')
    out.attachTrace = values.attachTrace
  return out
}

export interface SavedSupportDraft {
  entryKey: string
  idempotencyKey: string
  origin?: SupportPageContext
  values: {
    title: string
    body: string
    productScope: string
  } & OptionalDraftFields
}

/** Session recovery is optional. Storage denial must leave ordinary intake usable. */
export function saveSupportDraft(
  principalId: string,
  draft: SavedSupportDraft,
  now: number
) {
  if (!principalId) return
  const { title, body, productScope } = draft.values
  if (title.length > 500 || body.length > 20000 || productScope.length > 64)
    return
  try {
    sessionStorage.setItem(
      PREFIX + principalId,
      JSON.stringify({
        version: 1,
        principalId,
        updatedAt: now,
        entryKey: draft.entryKey,
        idempotencyKey: draft.idempotencyKey,
        origin: validatedSupportPageContext(draft.origin),
        values: {
          title,
          body,
          productScope,
          ...optionalDraftFields(draft.values)
        }
      })
    )
  } catch {
    // Quota/security denial disables recovery; no ticket content is logged.
  }
}

/** Expiration feedback contains no saved content or cross-entry information. */
export function recoverSupportDraft(
  principalId: string,
  entryKey: string,
  now: number
): { draft?: SavedSupportDraft; expired: boolean } {
  const unavailable = { expired: false }
  if (!principalId || !Number.isFinite(now)) return unavailable
  try {
    const raw = sessionStorage.getItem(PREFIX + principalId)
    if (!raw || raw.length > 40000) return unavailable
    const value = JSON.parse(raw)
    if (
      !value ||
      value.version !== 1 ||
      value.principalId !== principalId ||
      value.entryKey !== entryKey ||
      typeof value.updatedAt !== 'number' ||
      !Number.isFinite(value.updatedAt) ||
      now < value.updatedAt ||
      typeof value.idempotencyKey !== 'string' ||
      value.idempotencyKey.length > 128 ||
      !value.values ||
      typeof value.values.title !== 'string' ||
      value.values.title.length > 500 ||
      typeof value.values.body !== 'string' ||
      value.values.body.length > 20000 ||
      typeof value.values.productScope !== 'string' ||
      value.values.productScope.length > 64
    )
      return unavailable
    if (now - value.updatedAt >= TTL) {
      try {
        sessionStorage.removeItem(PREFIX + principalId)
      } catch {
        // Storage denial must not restore expired content or hide the notice.
      }
      return { expired: true }
    }
    return {
      expired: false,
      draft: {
        entryKey,
        idempotencyKey: value.idempotencyKey,
        origin: validatedSupportPageContext(value.origin),
        values: {
          ...optionalDraftFields(value.values),
          title: value.values.title,
          body: value.values.body,
          productScope: value.values.productScope
        }
      }
    }
  } catch {
    return unavailable
  }
}

export function loadSupportDraft(
  principalId: string,
  entryKey: string,
  now: number
): SavedSupportDraft | undefined {
  return recoverSupportDraft(principalId, entryKey, now).draft
}

export function clearSupportDraft(principalId: string) {
  try {
    sessionStorage.removeItem(PREFIX + principalId)
  } catch {
    /* Storage unavailable. */
  }
}

export function clearSupportDrafts() {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i)
      if (key?.startsWith(PREFIX)) sessionStorage.removeItem(key)
    }
  } catch {
    /* Storage unavailable; the principal check still prevents cross-user recovery. */
  }
}
